import NextApp, {AppProps} from 'next/app';
import Head from 'next/head';
import React, {useCallback, useEffect, useState} from 'react';
import Script from 'next/script';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react-lite';
import {initSentry} from '@/utils/sentry';
import Layout from '@/components/layoutV2';
import {
  getSingleUrlParam,
  isKrPublicPage,
  isCaPublicPage,
  getDomain,
} from '@/utils/url';
import {useRouter} from 'next/router';

import 'antd/dist/antd.css'; // do not remove
import '@fortawesome/fontawesome-svg-core/styles.css'; // do not remove
import './app.scss'; // do not remove

import {config} from '@fortawesome/fontawesome-svg-core';

import {MixPanel} from '@/utils/mixpanel';
import {DASHBOARD_PAGE_IS_OPENED} from '@/constants/events';
import {publicPages} from '@/utils/const';
import {getApiUrl, getTokenFromCookies} from '@/api/common-utils';
import {BaseApi} from '@/api/base-api';
import {profileApi} from '@/api/account';
import {linkgraphDomains} from '@/utils/router';
import userpilot from '@/utils/userpilot/userpilot';
import Intercom from '@intercom/messenger-js-sdk';
import {convertToUnixTimestamp} from '@/utils/moment';
import moment from 'moment';
import initOtelInBrowser, {useOtelRouterInstrumentation} from '@/utils/otel/client';
import {showCrisp, getHostName, isLocalSeoPublicWithDisabledLogo, authPages} from '@/utils/const';

config.autoAddCss = false;
// Initialize the base OpenTelemetry functionality
initOtelInBrowser();
initSentry();

type StaticComponents = {
    getInitialProps?: typeof NextApp.getInitialProps;
};
declare global {
    interface Window {
        Intercom: Function;
    }
}

const App: React.FC<AppProps> & StaticComponents = observer(
  ({Component, pageProps}: AppProps) => {
    const router = useRouter();
    // Initialize router instrumentation hook at top level
    useOtelRouterInstrumentation();
    const store = useStore(pageProps.initialState);
    const {
      countries: {getCountriesData, loadCountriesData},
      home: {setIsChildAccount},
      plans: {handlePayAnnual},
      settings: {
        customer: {
          getExternalSettings,
          profile,
          isGHLAccount,
          profile: {
            getVendastaClientDetails,
            setLogoInStore,
            logo,
            setSignInLogoInStore,
            setSignUpLogoInStore,
            setBrandColor,
            setDomainApiLoading,
          },
        },
      },
      siteAuditorV2: {
        setSelectedSiteAuditInStore,
        loadAuditedSitesListBasic,
        getAuditSitesList,
        getAuditSitesListBasic,
        siteAuditSelectedProperty,
      },
    } = useStore('');

    const today = new Date();
    const dd = today.getDate();
    const mm = today.getMonth();
    const yy = today.getFullYear();
    const token = getTokenFromCookies();
    const [favicon, setFavicon] = useState(null);
    const [customerId, setCustomerId] = useState<any>();
    const [currentDomain, setCurrentDomain] = useState('');

    const showCrispOnAgency = ['my.agencyremix.app'];
    const showCrispOnBrewroom = ['dashboard.thebrewroom.com'];
    const currentCustomerId = typeof window !== 'undefined' && localStorage.getItem('currentCustomerId');
    const storedDate = typeof window !== 'undefined' && Number(localStorage.getItem('date'));
    const isWhiteLabelDomain = !linkgraphDomains.includes(currentDomain);
    const canonicalURL = `${process.env.HOST_DASHBOARD}${router.asPath.split('?')[0]}`;

    useEffect(() => {
      if (router.isReady) {
        if (router.asPath.includes('otto-page-v2')) {
          const newPath = router.asPath.replace('otto-page-v2', 'seo-automation');
          router.replace(newPath);
        } else if (router.asPath.includes('otto-ppc')) {
          const newPath = router.asPath.replace('otto-ppc', 'google-ads-automation');
          router.replace(newPath);
        }
      }
    }, [router.isReady, router.asPath]);

    if (!storedDate && token && !profile.isWhitelabel) {
      localStorage.setItem('compDate', `${mm}/${dd}/${yy}`);
      typeof window !== 'undefined' && localStorage.setItem('date', String(dd));
    }

    const getPageUrlForMixpanel = (page: string) => {
      if (page === '/gsc/overview' || page === '/gsc') {
        return '/gsc/overview';
      } else if (page.startsWith('/research/keyword-researcher-old/detail/')) {
        return '/research/keyword-researcher-old/detail/';
      } else if (page.startsWith('/content/seo-content-assistant/')) {
        return '/content/seo-content-assistant/[articleID]';
      } else if (page.startsWith('/research/keyword-researcher-old/list/')) {
        return '/research/keyword-researcher-old/list/';
      } else {
        return page;
      }
    };

    // We have same function in /src/api/account/plans/plans-api.ts,
    // potentially we can replace it with the one from plans-api
    const getCustomerJwt = async (id: number) => {
      if (!isNaN(id)) {
        try {
          const baseUrl = getApiUrl(
            BaseApi.LINKGRAPH_ENDPOINT,
            '/api/customer',
          );
          const baseApi = new BaseApi();
          const response = await baseApi.axios.get(`${baseUrl}/child-accounts/${id}/`);
          return response.data;
        } catch (e) {
          return Promise.reject(e);
        }
      }
    };

    const getListAndUpdateSA = useCallback(async () => {
      if (getAuditSitesList?.length) {
        setSelectedSiteAuditInStore(getAuditSitesList[0]?.id);
      } else if (getAuditSitesListBasic?.length) {
        setSelectedSiteAuditInStore(getAuditSitesListBasic[0]?.id);
      } else {
        await loadAuditedSitesListBasic();
        if (getAuditSitesListBasic?.length) {
          setSelectedSiteAuditInStore(getAuditSitesListBasic[0]?.id);
        }
      }
    }, [getAuditSitesList, setSelectedSiteAuditInStore, getAuditSitesListBasic, loadAuditedSitesListBasic]);

    const getLogo = useCallback(async () => {
      try {
        setDomainApiLoading(true);
        const logos = await profileApi.getDomainLogos();
        if (logos) {
          if (logos?.faviconUrl) {
            setFavicon(logos?.faviconUrl);
          } else {
            setFavicon('/img/official-favicon-new.png');
          }
          setLogoInStore(`${logos?.logoUrl}`);
          setSignInLogoInStore(`${logos?.signupLogoUrl || logos?.logoUrl}`);
          setSignUpLogoInStore(`${logos?.signupLogoUrl || logos?.logoUrl}`);
          setBrandColor(logos?.brandColor);
        }
        setDomainApiLoading(false);
      } catch (e) {
        // eslint-disable-next-line
                console.warn("Error fetching logos", e);
      }
    }, [setDomainApiLoading, setFavicon, setLogoInStore, setSignInLogoInStore, setSignUpLogoInStore, setBrandColor]);

    useEffect(() => {
      getExternalSettings();
      if (storedDate && storedDate !== dd && token && !profile.isWhitelabel) {
        const comparisonDate =
                    typeof window !== 'undefined' &&
                    (localStorage.getItem('compDate') as any);
        const currentData = `${mm}/${dd}/${yy}` as any;
        const date1 = new Date(comparisonDate) as any;
        const date2 = new Date(currentData) as any;
        const getDiff = Math.abs(date2 - date1);
        const diffDays = Math.ceil(getDiff / (1000 * 60 * 60 * 24));

        if (diffDays >= 14) {
          localStorage.setItem('compDate', `${mm}/${dd}/${yy}`);
          localStorage.setItem('showConsultationModal', 'true');
        }
        typeof window !== 'undefined' &&
                    localStorage.setItem('date', String(dd));
      }
      const today = new Date();
      if (today.getDay() === 1) {
        const hasUserHiddenMondayMessage = localStorage.getItem('hasUserHiddenMondayMessage');
        if (hasUserHiddenMondayMessage === null) {
          localStorage.setItem('hasUserHiddenMondayMessage', 'false');
        }
      } else {
        localStorage.removeItem('hasUserHiddenMondayMessage');
      }
    }, []);

    useEffect(() => {
      const publicHash = getSingleUrlParam('public_hash');

      if (!publicHash) {
        if (
          showCrisp.includes(getHostName) ||
                    showCrispOnAgency.includes(getHostName) ||
                    showCrispOnBrewroom.includes(getHostName)
        ) {
          window['dataLayer'] = window['dataLayer'] || [];
        }
      }
    }, []);

    useEffect(() => {
      const promoCode = getSingleUrlParam('promo_code');
      if (promoCode) {
        localStorage.setItem('promotionCode', promoCode);
      }
      const checkPayAnnual = getSingleUrlParam('pay_annually');
      if (checkPayAnnual) {
        handlePayAnnual(true);
      }
      if (!getCountriesData?.length) {
        loadCountriesData();
      }
    }, [router, getCountriesData?.length, loadCountriesData, handlePayAnnual]);

    useEffect(() => {
      const {searchParams} = new URL(window.location.href);
      const customer = searchParams.getAll('customerId');
      if (customer.length) {
        setCustomerId(customer[0]);
        getCustomerJwt(Number(customer[0]) || Number(customerId));
      }
    }, [router.asPath, profile?.isParentAccount, siteAuditSelectedProperty, customerId, currentCustomerId, getListAndUpdateSA, setIsChildAccount]);

    useEffect(() => {
      if (!router.asPath.includes('site-audit/page-explorer')) {
        localStorage.removeItem('segmentData');
      }
    }, [router.asPath]);

    useEffect(() => {
      if (router.isReady && router?.pathname != '/login') {
        MixPanel.track(DASHBOARD_PAGE_IS_OPENED, {
          page: getPageUrlForMixpanel(window.location.pathname),
        });
      }
    }, [router.pathname, router.isReady]);

    useEffect(() => {
      const contentAssistantPublicPage =
                '/content/seo-content-assistant-public/';
      if (
        !publicPages.includes(location.pathname) &&
                router.asPath !== '/onboarding' &&
                router.asPath !== '/' &&
                !isKrPublicPage() &&
                !isCaPublicPage() &&
                !window.location.pathname.match(contentAssistantPublicPage) &&
                !router.asPath.includes('jwtToken')
      ) {
        localStorage.setItem('nextUrl', router.asPath);
      }
      if (!logo) {
        getLogo();
      }
    }, [router.pathname, router.asPath, logo, getLogo]);

    useEffect(() => {
      setCurrentDomain(getDomain(window.location.hostname));
    }, []);

    useEffect(() => {
      if (isGHLAccount !== undefined && isGHLAccount !== null) {
        if (isGHLAccount) {
          localStorage.setItem('isGHLAccount', 'true');
        } else {
          localStorage.removeItem('isGHLAccount');
        }
      }
    }, [isGHLAccount]);

    useEffect(() => {
      userpilot.initialize();

      const timer = setTimeout(() => {
        if (profile?.id) {
          userpilot.identify(
            profile?.id?.toString(),
            {
              name: profile?.firstName,
              email: profile?.emailAddress,
              created_at: profile?.joiningDate,
            },
          );
        }
      }, 1000);
      return () => clearTimeout(timer);
    }, [profile?.id, profile?.firstName, profile?.emailAddress, profile?.joiningDate, getHostName]);

    const intercomData = () => {
      const qp = `https://quantumpuppy.com/admin/customer/customer/${profile?.customerId}/change/`;
      return {
        'app_id': process.env.INTERCOM_APP_ID,
        'user_id': profile.id.toString(),
        'name': profile.firstName,
        'email': profile.emailAddress,
        'created_at': convertToUnixTimestamp(profile.joiningDate),
        'QP': qp,
        'Plan': profile.planName,
        'Agency_or_Brand': profile.agencyOrBrand,
        'Trial_or_Subscriber': profile.freeTrial,
        'Last_Payment': moment(profile.subscription?.lastPaidAt).format('YYYY-MM-DD'),
        'Next_Payment': moment(profile.subscription?.nextPaymentAt).format('YYYY-MM-DD'),
        'OTTO_Activations': profile.quotaUtilization?.ca?.allowedOttoAiSuggestions?.consumed,
        'AI_Quota': profile.quotaUtilization?.ca?.allowedOttoAiSuggestions?.consumed,
        'WL_Customer_or_Child_Account': profile.isWhitelabel ? 'WL' : profile.isParentAccount ? 'Parent Account' : 'Child Account',
        'Promo_Code': profile.promoCode,
        'Is_Admin': profile.roles?.includes('Admin') ? true : false};
    };

    useEffect(() => {
      if (profile?.id && (process.env.NODE_ENV === 'production' && showCrisp.includes(getHostName)) && !isLocalSeoPublicWithDisabledLogo(router) && !isGHLAccount) {
        // eslint-disable-next-line new-cap
        Intercom(intercomData());
      }
    }, [profile?.id, profile?.customerId, profile?.firstName, profile?.emailAddress, profile?.joiningDate, getHostName, isGHLAccount]);

    // Initialize OpenTelemetry
    useEffect(() => {
      // Router instrumentation is handled by hook at component top level

      // Set user context if profile is available and we're authenticated
      if (profile?.id) {
        // Import is inside useEffect to avoid SSR issues
        import('@/utils/otel/user-context').then(({setUserContext}) => {
          try {
            // Safely format dates with fallbacks
            const formatDate = dateValue => {
              if (!dateValue) return undefined;
              try {
                return moment(dateValue).isValid() ?
                  moment(dateValue).format('YYYY-MM-DD') :
                  undefined;
              } catch (err) {
                return undefined;
              }
            };

            // Safely get nested values with defaults
            const getNestedValue = (obj, path, defaultValue = undefined) => {
              try {
                const result = path.split('.')
                  .reduce((prev, curr) => (prev && prev[curr] !== undefined) ? prev[curr] : undefined, obj);
                return result !== undefined ? result : defaultValue;
              } catch (err) {
                return defaultValue;
              }
            };

            // Build user context with safer property access
            setUserContext({
              userId: profile.id?.toString(),
              email: profile.emailAddress,
              username: profile.firstName,
              organizationId: profile.customerId?.toString(),

              // Add additional Intercom metrics for debugging
              customerId: profile.customerId?.toString(),
              planName: profile.planName,
              agencyOrBrand: profile.agencyOrBrand,
              freeTrial: profile.freeTrial,
              lastPaymentDate: formatDate(profile.subscription?.lastPaidAt),
              nextPaymentDate: formatDate(profile.subscription?.nextPaymentAt),
              ottoActivations: getNestedValue(profile, 'quotaUtilization.ca.allowedOttoAiSuggestions.consumed'),
              aiQuota: getNestedValue(profile, 'quotaUtilization.ca.allowedOttoAiSuggestions.consumed'),
              isWhitelabel: profile.isWhitelabel,
              isParentAccount: profile.isParentAccount,
              promoCode: profile.promoCode,
              isAdmin: Array.isArray(profile.roles) ? profile.roles.includes('Admin') : false,
            });
          } catch (err) {
            // Log error but don't break the app
            console.debug?.('[otel-init] Failed to set user context:', err);
          }
        }).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.debug('Failed to load user context module:', err);
          }
        });
      }

      // No need for explicit cleanup here, the hook handles it
    }, [
      profile?.id,
      profile?.emailAddress,
      profile?.firstName,
      profile?.customerId,
      profile?.planName,
      profile?.agencyOrBrand,
      profile?.freeTrial,
      profile?.subscription?.lastPaidAt,
      profile?.subscription?.nextPaymentAt,
      profile?.quotaUtilization?.ca?.allowedOttoAiSuggestions?.consumed,
      profile?.isWhitelabel,
      profile?.isParentAccount,
      profile?.promoCode,
      profile?.roles,
    ]);
    return (
      <>
        <Head>
          <link rel='icon' href={isWhiteLabelDomain ? favicon : '/img/official-favicon-new.png'} sizes='32x32' />
          {
            (process.env.NODE_ENV === 'production' && showCrisp.includes(getHostName)) && !isLocalSeoPublicWithDisabledLogo(router) && ((typeof window !== 'undefined' && authPages.includes(window.location.pathname)) || isGHLAccount === false) ? <>
              <link rel='canonical' href={canonicalURL} key='canonical' />
              <script dangerouslySetInnerHTML={{__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-WGBMQTD');
              `}}/>
              <script src='/scripts/fpr_main.js' async></script>
              <script
                src={`https://cdn.firstpromoter.com/fpr.js`}
                async
              ></script>
              {/* Meta Pixel Code */}
              <script
                dangerouslySetInnerHTML={{
                  __html: `!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1529626644659448');
            fbq('track', 'PageView');`,
                }}
              ></script>
              <noscript>
                <img
                  height='1'
                  width='1'
                  style={{display: 'none'}}
                  src='https://www.facebook.com/tr?id=1529626644659448&ev=PageView&noscript=1'
                  alt='Meta Pixel'
                />
              </noscript>
              {/* End Meta Pixel Code */}
            </> : (
              ''
            )}
          {process.env.NODE_ENV === 'production' && <script type='text/javascript' dangerouslySetInnerHTML={{
            __html: `(function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "p5lqqqvxas");`}} />}
        </Head>
        <Layout store={store} pathname={pageProps.pathname}>
          <Component {...pageProps} rootStore={store} />

          {showCrisp.includes(getHostName) ? (
            <noscript>
              <iframe
                src='https://www.googletagmanager.com/ns.html?id=GTM-WGBMQTD'
                height='0'
                width='0'
                style={{display: 'none', visibility: 'hidden'}}
              />
            </noscript>
          ) : (
            ''
          )}
        </Layout>
        {getVendastaClientDetails?.isVendastaClient && (
          <Script
            id='vendasta-navbar'
            src='//www.cdnstyles.com/static/product_navbar/v1/product_navbar.js'
            strategy='lazyOnload'
            data-url={`${getVendastaClientDetails.dataUrl}`}
            data-account-id={`${getVendastaClientDetails.dataAccountId}`}
            data-app-id={`${getVendastaClientDetails.dataAppId}`}
          />
        )}
      </>
    );
  },
);

App.getInitialProps = async appContext => {
  if (appContext.ctx.asPath === '/readiness') {
    appContext.ctx.res.writeHead(200);
    appContext.ctx.res.end();
    return {pageProps: {}};
  }

  const appProps = await NextApp.getInitialProps?.(appContext);

  return {...appProps};
};

export default App;
